import { parseProduct, isCannabisProduct } from "@shared/lib/product-parser";

// Color keywords for detection (matching admin panel)
export const COLOR_KEYWORDS = [
  'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink', 'black', 'white', 'gray', 'grey',
  'brown', 'beige', 'tan', 'navy', 'maroon', 'teal', 'cyan', 'magenta', 'lime', 'olive',
  'silver', 'gold', 'bronze', 'copper', 'ivory', 'cream', 'coral', 'salmon', 'turquoise',
  'violet', 'indigo', 'amber', 'burgundy', 'charcoal', 'jet black', 'midnight black', 'clear'
];

// Size patterns for detection (matching admin panel)
export const SIZE_PATTERNS = [
  'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '2xl', '3xl', '4xl',
  'small', 'medium', 'large', 'extra small', 'extra large',
  'one size', 'onesize', 'os',
  /\d+mm/i, // e.g., 63mm, 160mm
  /\d+cm/i, // e.g., 10cm
  /\d+"?/i, // e.g., 10", 10
];

// Weight patterns for detection
const WEIGHT_PATTERNS = [
  /\d+x?\d*\.?\d*\s*g$/i,  // e.g., "1g", "28g", "10x0.35g"
  /\d+\s*pack$/i,          // e.g., "10 Pack"
  /\d+\s*ml$/i,            // e.g., "100ml"
  /\d+\s*mg$/i,            // e.g., "100mg"
];

export function isColor(value: string): boolean {
  const lower = value.toLowerCase().trim();
  return COLOR_KEYWORDS.some(keyword => lower === keyword || lower.includes(keyword));
}

export function isSize(value: string): boolean {
  const lower = value.toLowerCase().trim();
  // Check against size patterns
  if (SIZE_PATTERNS.some(pattern => {
    if (typeof pattern === 'string') {
      return lower === pattern || lower.includes(pattern);
    } else {
      return pattern.test(lower);
    }
  })) {
    return true;
  }
  // Exclude weight patterns
  return !WEIGHT_PATTERNS.some(pattern => pattern.test(lower));
}

export function isWeight(value: string): boolean {
  return WEIGHT_PATTERNS.some(pattern => pattern.test(value));
}

// Extract color and size from a product (matching admin panel logic)
export function extractVariants(
  productName: string,
  classificationName?: string | null,
  brandName?: string | null,
  sizeOrWeight?: string | null
): { color: string | null; size: string | null } {
  const result = { color: null as string | null, size: null as string | null };

  try {
    const parsed = parseProduct(productName, classificationName || null, brandName || null, sizeOrWeight || null);

    // Check variant1 (typically color for clothing)
    if (parsed.variant1) {
      if (isColor(parsed.variant1)) {
        result.color = parsed.variant1;
      } else if (isSize(parsed.variant1)) {
        result.size = parsed.variant1;
      }
    }

    // Check variant2 (typically size for clothing)
    if (parsed.variant2) {
      if (isSize(parsed.variant2)) {
        result.size = parsed.variant2;
      } else if (isColor(parsed.variant2) && !result.color) {
        result.color = parsed.variant2;
      }
    }

    // Check sizeOrWeight field
    if (sizeOrWeight) {
      if (isSize(sizeOrWeight) && !result.size) {
        result.size = sizeOrWeight;
      }
    }
  } catch {
    // Metadata parse failed — fall through to segment-based extraction below
  }

  // Fallback: try to extract from product name segments
  const segments = productName.split('|').map(s => s.trim()).filter(s => s.length > 0);

  // For 4-segment names: Brand | Product | Variant1 | Variant2
  if (segments.length >= 4 && !result.color && !result.size) {
    const segment3 = segments[2];
    const segment4 = segments[3];

    // Try to identify segment 3
    if (isColor(segment3) && !result.color) {
      result.color = segment3;
    } else if (isSize(segment3) && !result.size) {
      result.size = segment3;
    }

    // Try to identify segment 4
    if (isSize(segment4) && !result.size) {
      result.size = segment4;
    } else if (isColor(segment4) && !result.color) {
      result.color = segment4;
    }
  }

  return result;
}

// Extract all available colors and sizes from size options
export function extractColorsAndSizes(
  sizeOptions: Array<{
    productId: string;
    packageId: string | null;
    productSlug: string | null;
    originalProductName?: string | null;
    sizeOrWeight?: string | null;
    classificationName?: string | null;
    brandName?: string | null;
    color?: string | null;
    size?: string | null;
  }>
): { colors: string[]; sizes: string[]; variantsByColor: Map<string, typeof sizeOptions> } {
  const colorsSet = new Set<string>();
  const sizesSet = new Set<string>();
  const variantsByColor = new Map<string, typeof sizeOptions>();

  sizeOptions.forEach(option => {
    // Extract variants if not already present
    let color = option.color;
    let size = option.size;

    if (!color || !size) {
      const extracted = extractVariants(
        option.originalProductName || '',
        option.classificationName || null,
        option.brandName || null,
        option.sizeOrWeight || null
      );
      if (!color) color = extracted.color;
      if (!size) size = extracted.size;
    }

    // Add to sets
    if (color) {
      colorsSet.add(color);
      if (!variantsByColor.has(color)) {
        variantsByColor.set(color, []);
      }
      variantsByColor.get(color)!.push(option);
    }
    if (size) {
      sizesSet.add(size);
    }
  });

  const colors = Array.from(colorsSet).sort();
  const sizes = Array.from(sizesSet).sort((a, b) => {
    // Sort sizes: numeric first, then alphabetical
    const aNum = parseFloat(a.replace(/[^0-9.]/g, '')) || 0;
    const bNum = parseFloat(b.replace(/[^0-9.]/g, '')) || 0;
    if (aNum !== 0 || bNum !== 0) {
      return aNum - bNum;
    }
    // Size order: XS, S, M, L, XL, XXL, etc.
    const sizeOrder = ['xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '2xl', '3xl', '4xl', 'small', 'medium', 'large', 'extra small', 'extra large', 'one size'];
    const aIndex = sizeOrder.findIndex(size => a.toLowerCase().includes(size));
    const bIndex = sizeOrder.findIndex(size => b.toLowerCase().includes(size));
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  return { colors, sizes, variantsByColor };
}

// Helper function to convert RGB to hex
function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('')}`;
}

// Get color hex code for display
export function getColorHex(color: string): string {
  if (!color) return '#000000';
  
  const colorNameLower = color.toLowerCase().trim();
  
  // Color name to RGB mapping (matching product details page and admin panel)
  const colorMap: Record<string, [number, number, number]> = {
    'pink': [255, 192, 203],
    'white': [255, 255, 255],
    'yellow': [255, 255, 0],
    'red': [255, 0, 0],
    'blue': [166, 185, 225], // Updated
    'green': [0, 128, 0],
    'black': [0, 0, 0],
    'gray': [128, 128, 128],
    'grey': [128, 128, 128],
    'purple': [128, 0, 128],
    'orange': [255, 165, 0],
    'brown': [165, 42, 42],
    'beige': [245, 245, 220],
    'navy': [0, 0, 128],
    'maroon': [128, 0, 0],
    'teal': [0, 128, 128],
    'coral': [255, 127, 80],
    'mint': [189, 252, 201],
    'lavender': [230, 230, 250],
    'tan': [210, 180, 140],
    'khaki': [240, 230, 140],
    'olive': [128, 128, 0],
    'burgundy': [128, 0, 32],
    'charcoal': [54, 69, 79],
    'cream': [255, 253, 208],
    'ivory': [255, 255, 240],
    'peach': [255, 218, 185],
    'salmon': [250, 128, 114],
    'turquoise': [64, 224, 208],
    'violet': [238, 130, 238],
    'amber': [255, 191, 0],
    'aqua': [0, 255, 255],
    'lime': [0, 255, 0],
    'magenta': [255, 0, 255],
    'cyan': [0, 255, 255],
    'indigo': [75, 0, 130],
    'fuchsia': [255, 0, 255],
    'gold': [255, 215, 0],
    'silver': [192, 192, 192],
    'bronze': [205, 127, 50],
    'copper': [184, 115, 51],
    'dark heather': [105, 105, 105],
    'heather': [182, 182, 182],
    'light heather': [211, 211, 211],
    'forest green': [44, 62, 47], // Updated
    'dark green': [0, 100, 0],
    'light green': [144, 238, 144],
    'emerald': [80, 200, 120],
    'sage': [188, 184, 138],
    'mint green': [152, 251, 152],
    'hunter green': [53, 94, 59],
    'army green': [75, 83, 32],
    'dark blue': [0, 0, 139],
    'light blue': [173, 216, 230],
    'sky blue': [135, 206, 235],
    'royal blue': [65, 105, 225],
    'navy blue': [0, 0, 128],
    'powder blue': [176, 224, 230],
    'dark gray': [169, 169, 169],
    'light gray': [211, 211, 211],
    'charcoal gray': [54, 69, 79],
    'dark grey': [169, 169, 169],
    'light grey': [211, 211, 211],
    'charcoal grey': [54, 69, 79],
    'dark brown': [101, 67, 33],
    'light brown': [181, 101, 29],
    'chocolate': [123, 63, 0],
    'coffee': [111, 78, 55],
    'caramel': [255, 213, 154],
    'dark red': [139, 0, 0],
    'light red': [255, 182, 193],
    'crimson': [220, 20, 60],
    'scarlet': [255, 36, 0],
    'burgundy red': [128, 0, 32],
    'dark pink': [231, 84, 128],
    'light pink': [255, 182, 193],
    'hot pink': [255, 105, 180],
    'rose': [255, 0, 127],
    'blush': [222, 93, 131],
    'dark purple': [128, 0, 128],
    'light purple': [221, 160, 221],
    'lavender purple': [150, 123, 182],
    'dark orange': [255, 140, 0],
    'light orange': [255, 200, 124],
    'burnt orange': [204, 85, 0],
    'dark yellow': [184, 134, 11],
    'light yellow': [255, 255, 224],
    'mustard': [255, 219, 88],
    'dark black': [0, 0, 0],
    'jet black': [20, 20, 20],
    'midnight black': [25, 25, 112],
    'clear': [255, 255, 255],
  };
  
  // Try exact match first
  if (colorMap[colorNameLower]) {
    const [r, g, b] = colorMap[colorNameLower];
    return rgbToHex(r, g, b);
  }
  
  // Try partial match (e.g., "dark heather" contains "heather")
  for (const [key, rgb] of Object.entries(colorMap)) {
    if (colorNameLower.includes(key) || key.includes(colorNameLower)) {
      const [r, g, b] = rgb;
      return rgbToHex(r, g, b);
    }
  }
  
  // Default to gray if no match
  return '#CCCCCC';
}

/**
 * Generate group key for products (matching backend logic).
 *
 * This ensures products are grouped the same way on client and backend.
 */
export function generateProductGroupKey(
  productName: string,
  classificationName: string | null | undefined,
  brandName: string | null | undefined,
  masterProductId: number | null | undefined,
  sizeOrWeight?: string | null
): string {
  const parsed = parseProduct(
    productName,
    classificationName || null,
    brandName || null,
    sizeOrWeight || null
  );
  
  const parsedBrand = (parsed.brand || brandName || '').toLowerCase().trim() || 'NO_BRAND';
  const parsedProductName = (parsed.productName || '').toLowerCase().trim() || 'NO_PRODUCT_NAME';
  const classKey = (classificationName || '').toLowerCase().trim() || 'NO_CLASS';
  
  const isAccessory = !isCannabisProduct(classificationName);
  
  if (isAccessory) {
    // Accessories/Clothing: parsedBrand + parsedProductName + classificationName (NO masterProductId)
    return `${parsedBrand}|${parsedProductName}|${classKey}`;
  } else if (masterProductId !== null && masterProductId !== undefined) {
    // Cannabis: masterProductId + parsedBrand + parsedProductName + classificationName
    return `${String(masterProductId)}|${parsedBrand}|${parsedProductName}|${classKey}`;
  } else {
    // No masterProductId and not accessory - use productId as unique key (each product is its own group)
    // This case should not happen in practice, but we return a unique key
    return `SINGLE_${parsedBrand}|${parsedProductName}|${classKey}`;
  }
}

